import React, { forwardRef } from 'react';
import { IonIcon } from '@ionic/react';
import { checkmarkCircle, alertCircleOutline } from 'ionicons/icons';
import './Field.css';

/**
 * Field — atomic dark-theme form control. Wraps <input> or <textarea>
 * with the .mkt-field class so every form across the app gets the same
 * surface, border, padding, focus, hover, and disabled treatment.
 *
 * Why a component and not just a class:
 *   - The class alone loses to other CSS rules with higher specificity
 *     (e.g. `.form-group input`). The component centralises the wiring
 *     so call sites can't accidentally end up with a 0,1,1 selector
 *     overriding the 0,1,0 atom rule.
 *   - The optional `status` slot (✓ / ✗ icon overlay) is a recurring
 *     pattern; baking it into the component avoids each consumer
 *     re-implementing the absolute-positioned icon wrapper.
 *   - One import to update if the visual ever changes.
 *
 * Props:
 *   as            — 'input' (default) | 'textarea'
 *   status        — 'ok' | 'error' | null  (right-edge icon overlay,
 *                                            input-variant only)
 *   className     — extra class names appended after the atom classes
 *   ...rest       — every other prop is forwarded to the underlying
 *                   element (value, onChange, placeholder, disabled,
 *                   onKeyDown, autoComplete, spellCheck, rows, etc.)
 */
const Field = forwardRef(function Field(
  { as = 'input', status = null, className = '', ...rest },
  ref
) {
  const isTextarea = as === 'textarea';
  const variant = isTextarea ? 'mkt-field--textarea' : 'mkt-field--input';
  const classes = ['mkt-field', variant, className].filter(Boolean).join(' ');

  if (isTextarea) {
    // Textareas don't carry a status icon overlay — the visual cue for
    // long-form prose belongs in a separate helper line below the
    // field, not crammed inside it.
    return <textarea ref={ref} className={classes} {...rest} />;
  }

  // Input: optional status icon wrapper. Bare <input> when no status
  // so call sites that don't care about validation don't render a
  // useless positioning span.
  if (!status) {
    return <input ref={ref} className={classes} {...rest} />;
  }

  return (
    <span className="mkt-field-input-wrap">
      <input ref={ref} className={classes} {...rest} />
      <span
        className={`mkt-field-status mkt-field-status--${status}`}
        aria-hidden="true"
      >
        <IonIcon icon={status === 'ok' ? checkmarkCircle : alertCircleOutline} />
      </span>
    </span>
  );
});

export default Field;
